import { useCallback, useEffect, useMemo, useState } from 'react';
import { cx } from '@emotion/css';
import { alwaysFirst } from '@helpers/data';
import { isArrowDown, isArrowUp, isEnter, isSpace, isTab } from '@helpers/dom';
import { ExcludeNil } from '@types';
import { Dropdown, DropdownOnClose } from '@kit/dropdown';
import { BaseInputOnKeyDown, BaseInputProps } from '@kit/inputs/base-input';
import { BaseSelectInput, BaseSelectInputOnChange } from '@kit/inputs/base-select-input';
import { SelectInputOptions } from './select-input-options';
import { SelectInputBaseChevronIconStyled, selectInputBaseInputInputClassName, SelectInputBaseInputStyled, SelectInputDropdownStyled, SelectInputStyled } from './styled';
import { SelectInputProps, SelectInputValue } from './types';
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
export function SelectInput<V extends SelectInputValue>(props: SelectInputProps<V>): React.ReactElement<SelectInputProps<V>> {
  const {
    className,
    inputClassName,
    defaultValue,
    value: originValue,
    options,
    openNative,
    opened: defaultOpened,
    hideValue,
    dropdownPosition,
    disabled,
    selectRef,
    inputRef,
    onChange,
    onBlur,
    ...restProps
  } = props;
  const [opened, setOpened] = useState(defaultOpened);
  const [value, setValue] = useState(originValue ?? defaultValue);
  useEffect(() => {
    if (defaultValue !== undefined) {
      return;
    }
    setValue(originValue);
  }, [defaultValue, originValue]);
  const selectedOption = useMemo(() => options.find(option => option.value === value), [options, value]);
  const selectedIndex = useMemo(() => selectedOption ? options.indexOf(selectedOption) : 0, [options, selectedOption]);
  const textValue = useMemo(() => {
    if (hideValue) {
      return '';
    }
    return selectedOption?.label ?? '';
  }, [hideValue, selectedOption]);
  const [hoveredIndex, setHoveredIndex] = useState(selectedIndex);
  const optionsLength = options.length;
  const open = useCallback(() => {
    if (disabled) {
      return;
    }
    setHoveredIndex(selectedIndex);
    setOpened(true);
  }, [selectedIndex, disabled]);
  const close = useCallback(() => {
    setOpened(false);
  }, []);
  const toggle: ExcludeNil<BaseInputProps['onClick']> = useCallback(event => {
    event.stopPropagation();
    if (opened) {
      close();
    } else {
      open();
    }
  }, [close, open, opened]);
  const closeAndBlur: DropdownOnClose = useCallback(event => {
    close();
    onBlur?.(event);
  }, [close, onBlur]);
  const select = useCallback((value: V, event: React.MouseEvent | React.KeyboardEvent) => {
    setValue(value);
    onChange?.(value, event);
  }, [onChange]);
  const change: BaseSelectInputOnChange<V> = useCallback((value, event) => {
    setValue(alwaysFirst(value));
    onChange?.(alwaysFirst(value), event);
  }, [onChange]);
  const keyDown: BaseInputOnKeyDown = useCallback(event => {
    if (isTab(event)) {
      close();
      return;
    }
    event.preventDefault();
    if (!opened && (isArrowUp(event) || isArrowDown(event))) {
      open();
      return;
    }
    if (opened && isArrowUp(event)) {
      if (hoveredIndex <= 0) {
        setHoveredIndex(optionsLength - 1);
        return;
      }
      setHoveredIndex(hoveredIndex - 1);
    }
    if (opened && isArrowDown(event)) {
      if (hoveredIndex >= optionsLength - 1) {
        setHoveredIndex(0);
        return;
      }
      setHoveredIndex(hoveredIndex + 1);
    }
    if (opened && (isEnter(event) || isSpace(event))) {
      select(options[hoveredIndex].value, event);
      close();
      return;
    }
  }, [close, hoveredIndex, open, opened, options, optionsLength, select]);
  const renderDropdown = useCallback(() => {
    return _jsx(SelectInputDropdownStyled, {
      children: _jsx(SelectInputOptions, {
        options: options,
        selectedIndex: selectedIndex,
        hoveredIndex: hoveredIndex,
        onSelect: (value, event) => {
          select((value as V), event);
          close();
        },
        onHover: setHoveredIndex
      })
    });
  }, [close, hoveredIndex, options, select, selectedIndex]);
  const icon = useMemo(() => _jsx(SelectInputBaseChevronIconStyled, {
    opened: opened
  }), [opened]);
  if (openNative) {
    return _jsx(BaseSelectInput //
    , {
      selectRef: selectRef,
      className: className,
      inputClassName: cx(selectInputBaseInputInputClassName, inputClassName),
      value: value,
      options: options,
      icon: icon,
      hideValue: hideValue,
      disabled: disabled,
      onChange: change,
      onBlur: onBlur,
      ...restProps
    });
  }
  return _jsx(SelectInputStyled, {
    className: className,
    children: _jsx(Dropdown, {
      renderDropdown: renderDropdown,
      position: dropdownPosition,
      opened: opened,
      onOpen: open,
      onClose: closeAndBlur,
      minWidth: "100%",
      maxWidth: "100%",
      disabled: disabled,
      children: _jsx(SelectInputBaseInputStyled //
      , {
        inputRef: inputRef,
        inputClassName: cx(selectInputBaseInputInputClassName, inputClassName),
        value: textValue,
        icon: icon,
        readOnly: true,
        disabled: disabled,
        onKeyDown: keyDown,
        onClick: toggle,
        ...restProps
      })
    })
  });
}